import { Text } from "@smoothincorg/smooth-ui"
import React, { useEffect } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { BorderedButton } from "src/components/Button"
import { Card } from "src/components/page-directory/Card"
import { InputField } from "src/components/InputField"
import { SubmitButton } from "src/features/lifftter/components/SubmitButton"
import { useAddressSWR } from "src/hooks/useAddressSWR"
import { DestinationAddress } from "src/hooks/useDestinationAddressSWR"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { camelCase } from "src/lib/aikagi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"
import { V1_OPE_URL } from "src/lib/constants/value"
import useSWR from "swr"
import { getZenkakuFormUtils } from "src/lib/utils/form"
import { useEditingId } from "../../hooks/useEditingId"
import { ModalEditableForm } from "../ModalEditableForm/ModalEditableForm"
import * as styles from "./MovingApplications.css"
import { useNotification } from "@smoothincorg/smooth-ui"

export const DestinationAddresses: React.FC<{
  movingApplicationId: number
}> = ({ movingApplicationId }) => {
  const resourceName = `MovingApplication.${movingApplicationId}.DestinationAddress`
  const { setEditingId } = useEditingId(resourceName)

  const { getWithToken, postWithToken, patchWithToken } = useSmoothApi()

  const destinationAddressSWR = useSWR(
    `${SMOOTH_SMOOTHER_API_URL}/moving_applications/${movingApplicationId}/destination_address`,
    async (url) => {
      const response = await getWithToken(url)
      return camelCase(response.data["data"]) as DestinationAddress
    }
  )

  const destinationAddress = destinationAddressSWR.data

  const destinationAddressForm = useForm<DestinationAddress>({
    mode: "onChange",
  })

  const { registerAutoFillAsZenkaku } = getZenkakuFormUtils(destinationAddressForm, {
    enableAutocomplete: true,
  })

  const destinationAddressPostCodeForm = useForm<{
    zipCode: string
  }>({
    defaultValues: {
      zipCode: "",
    },
  })

  const zipCodeValue = destinationAddressPostCodeForm.watch("zipCode")

  const addressSWR = useAddressSWR(zipCodeValue)

  const { showAlert, showSnackbar } = useNotification()

  const onSubmit: SubmitHandler<DestinationAddress> = async (data) => {
    const shouldPatch = destinationAddressSWR.data?.id > 0
    const action = shouldPatch ? patchWithToken : postWithToken

    const payload = {
      destination_address: {
        prefecture: data.prefecture,
        line_1: data.line1,
        line_2: data.line2,
        line_3: data.line3,
        building_name: data.buildingName,
        room_number: data.roomNumber,
        postcode_head: zipCodeValue.slice(0, 3),
        postcode_tail: zipCodeValue.slice(3, 7),
      },
    }

    // SnakeCase関数使うとline1_kanaとかになるので手動で変換
    try {
      await action(
        `${SMOOTH_SMOOTHER_API_URL}/moving_applications/${movingApplicationId}/destination_address`,
        payload
      )
    } catch (e) {
      const error = e as Error
      showAlert("エラーが発生しました！: " + error.message)
      return
    }

    showSnackbar("保存しました！", "success")

    destinationAddressSWR.mutate()
  }

  useEffect(() => {
    if (destinationAddress) {
      destinationAddressForm.reset(destinationAddress)
    }
  }, [destinationAddress, destinationAddressForm])

  useEffect(() => {
    if (destinationAddressSWR.data) {
      destinationAddressPostCodeForm.reset({
        zipCode: destinationAddressSWR.data.postcodeHead + destinationAddressSWR.data.postcodeTail,
      })
    }
  }, [destinationAddressPostCodeForm, destinationAddressSWR.data])

  useEffect(() => {
    const result = addressSWR?.results?.[0]

    if (result) {
      destinationAddressForm.reset((c) => ({
        ...c,
        prefecture: c.prefecture || result.address1,
        line1: c.line1 || result.address2,
        line2: c.line2 || result.address3,
      }))
    }
  }, [addressSWR?.results, destinationAddressForm])

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>
        転居先住所
        {!destinationAddress && (
          <BorderedButton
            onClick={(e) => {
              e.preventDefault()
              setEditingId((c) => {
                if (c === 0) return null
                return 0
              })
            }}
            style={{ marginLeft: "1rem", background: "#ffffff" }}
          >
            新規作成
          </BorderedButton>
        )}
      </h2>
      <section>
        <Card key={destinationAddress?.id} className={styles.destinationAddress}>
          {!destinationAddress && <p>転居先住所が登録されていません</p>}
          <ModalEditableForm
            resourceName={resourceName}
            resource={destinationAddress}
            opeHref={
              destinationAddress
                ? V1_OPE_URL + "/admin/destination_addresses/" + destinationAddress.id
                : null
            }
            items={
              destinationAddress
                ? [
                    ["prefecture", destinationAddress?.prefecture],
                    ["line1", destinationAddress?.line1],
                    ["line2", destinationAddress?.line2],
                    ["line3", destinationAddress?.line3],
                    ["postcodeHead", destinationAddress?.postcodeHead],
                    ["postcodeTail", destinationAddress?.postcodeTail],
                    ["buildingName", destinationAddress?.buildingName],
                    ["roomNumber", destinationAddress?.roomNumber],
                  ]
                : []
            }
            onSubmit={destinationAddressForm.handleSubmit(onSubmit)}
          >
            <InputField
              className={styles.field}
              label="郵便番号"
              {...destinationAddressPostCodeForm.register("zipCode")}
              disabled={destinationAddressSWR.isValidating}
            />
            <div className={styles.field} style={{ position: "relative" }}>
              <Text size={12}>
                {addressSWR.isValidating ? "loading..." : null}
                {zipCodeValue && addressSWR.couldNotFindResults ? "住所見つけられなかった…" : null}
              </Text>
            </div>
            <InputField
              className={styles.field}
              label="都道府県"
              {...registerAutoFillAsZenkaku("prefecture")}
              disabled={addressSWR.isValidating}
              validationMessage={destinationAddressForm.formState.errors.prefecture?.message}
            />
            <InputField
              className={styles.field}
              label="市区町村"
              {...registerAutoFillAsZenkaku("line1")}
              disabled={addressSWR.isValidating}
              validationMessage={destinationAddressForm.formState.errors.line1?.message}
            />
            <InputField
              className={styles.field}
              label="町名など"
              {...registerAutoFillAsZenkaku("line2")}
              disabled={addressSWR.isValidating}
              validationMessage={destinationAddressForm.formState.errors.line2?.message}
            />
            <InputField
              label="町名以降"
              className={styles.field}
              {...registerAutoFillAsZenkaku("line3")}
              placeholder="5-20-6"
              validationMessage={destinationAddressForm.formState.errors.line3?.message}
            />
            <InputField
              className={styles.field}
              label="建物名"
              {...registerAutoFillAsZenkaku("buildingName")}
              placeholder="宇田川町ビルディング"
              validationMessage={destinationAddressForm.formState.errors.buildingName?.message}
            />
            <InputField
              className={styles.field}
              label="部屋番号"
              {...registerAutoFillAsZenkaku("roomNumber")}
              placeholder="３０１"
              validationMessage={destinationAddressForm.formState.errors.roomNumber?.message}
            />
            <SubmitButton
              onClick={async (e) => {
                e.preventDefault()
                const result = await destinationAddressForm.trigger()

                if (!result) {
                  showAlert("エラーが発生しました！: 入力内容を確認してください")
                }

                if (result) {
                  destinationAddressForm.handleSubmit(onSubmit)(e)
                }
              }}
              style={{ width: 200, margin: "0 auto" }}
            >
              保存
            </SubmitButton>
          </ModalEditableForm>
        </Card>
      </section>
    </div>
  )
}
